import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  Tooltip
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle,
  Phone,
  Email
} from '@mui/icons-material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { logoWhiteHorizontal, LOGO_PATHS } from '../../assets/images';
import planeGif from '../../assets/gifs/plane.gif';
import { useTranslation } from 'react-i18next';
import { languageSettingsService } from '../../services/languageSettingsService';

const Header: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [languageVisibility, setLanguageVisibility] = useState<{ tr: boolean; en: boolean; ar: boolean }>({ tr: true, en: true, ar: true });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, userProfile, signOut } = useAuth();

  // Dil butonları için veriler
  const languages = [
    { code: 'tr' as const, flag: 'tr' },
    { code: 'en' as const, flag: 'en' },
    { code: 'ar' as const, flag: 'ar' }
  ];

  useEffect(() => {
    const fetchLanguageSettings = async () => {
      try {
        const settings = await languageSettingsService.getLanguageSettings();
        if (settings && settings.languages) {
          setLanguageVisibility(settings.languages);
        }
      } catch (err) {
        console.error('Language settings fetch error:', err);
      }
    };

    fetchLanguageSettings();
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      handleProfileMenuClose();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const menuItems = [
    { text: t('nav.home'), path: '/' },
    { text: t('nav.tours'), path: '/tours' },
    { text: t('nav.blog'), path: '/blog' },
    { text: t('nav.about'), path: '/about' },
    { text: t('nav.contact'), path: '/contact' },
    { text: t('nav.custom_tour'), path: '/custom-tour-request' },
  ];

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Drawer Header */}
      <Box sx={{ 
        p: 3, 
        borderBottom: '1px solid var(--gray-shadow)',
        backgroundColor: 'var(--navy-blue)'
      }}>
        <Box
          component="img"
          src={logoWhiteHorizontal}
          alt="HatTravel Logo"
          sx={{
            height: 32,
            width: 'auto',
            maxWidth: 160,
            objectFit: 'contain'
          }}
        />
      </Box>

      {/* Language Switcher for Drawer */}
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', gap: 1, borderBottom: '1px solid var(--gray-shadow)' }}>
        {languages
          .filter(l => languageVisibility[l.code])
          .map(l => (
            <Tooltip key={l.code} title={l.code.toUpperCase()} placement="top">
              <IconButton
                size="small"
                onClick={() => i18n.changeLanguage(l.code)}
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  fontSize: '1.1rem',
                  border: '1px solid var(--gray-shadow)',
                  backgroundColor: i18n.language.startsWith(l.code) ? 'var(--cobalt-blue)' : 'transparent',
                  color: i18n.language.startsWith(l.code) ? 'white' : 'var(--light-gray-white)',
                  '&:hover': {
                    backgroundColor: i18n.language.startsWith(l.code) ? 'var(--cobalt-blue)' : 'rgba(255,255,255,0.1)'
                  }
                }}
                aria-label={l.code}
              >
                {l.flag}
              </IconButton>
            </Tooltip>
          ))}
      </Box>

      {/* Menu Items */}
      <List sx={{ flexGrow: 1, pt: 2 }}>
        {menuItems.map((item) => (
          <ListItem
            key={item.text}
            component={Link}
            to={item.path}
            onClick={handleDrawerToggle}
            sx={{
              mx: 2,
              mb: 1,
              borderRadius: 2,
              color: 'var(--light-gray-white)',
              backgroundColor: location.pathname === item.path ? 'var(--cobalt-blue)' : 'transparent',
              '&:hover': {
                backgroundColor: 'var(--cobalt-blue)',
                transform: 'translateX(4px)',
                transition: 'all 0.2s ease-in-out'
              },
              transition: 'all 0.2s ease-in-out'
            }}
          >
            <ListItemText 
              primary={item.text}
              sx={{
                '& .MuiListItemText-primary': {
                  fontWeight: location.pathname === item.path ? 500 : 300,
                  fontSize: '1.1rem',
                  whiteSpace: 'nowrap'
                }
              }}
            />
          </ListItem>
        ))}
      </List>

      {/* User Section */}
      {currentUser ? (
        <Box sx={{ 
          p: 3, 
          borderTop: '1px solid var(--gray-shadow)',
          backgroundColor: 'rgba(0, 60, 148, 0.1)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Avatar
              sx={{ width: 40, height: 40, bgcolor: 'var(--light-blue-cta)' }}
            >
              {userProfile?.displayName?.charAt(0) || currentUser.phoneNumber?.charAt(0) || 'U'}
            </Avatar>
            <Box>
              <Typography variant="body2" sx={{ color: 'var(--light-gray-white)', fontWeight: 600 }}>
                {userProfile?.displayName || 'Kullanıcı'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'var(--light-gray-white)', opacity: 0.7 }}>
                {userProfile?.email || currentUser.email}
              </Typography>
            </Box>
          </Box>
          
          <Button
            component={Link}
            to="/profile"
            onClick={handleDrawerToggle}
            fullWidth
            variant="outlined"
            sx={{
              mb: 1,
              borderColor: 'var(--gray-shadow)',
              color: 'var(--light-gray-white)',
              borderRadius: 2,
              '&:hover': {
                borderColor: 'var(--cobalt-blue)',
                backgroundColor: 'var(--cobalt-blue)'
              }
            }}
          >
            Profil
          </Button>
          
          <Button
            component={Link}
            to="/booking-history"
            onClick={handleDrawerToggle}
            fullWidth
            variant="outlined"
            sx={{
              mb: 2,
              borderColor: 'var(--gray-shadow)',
              color: 'var(--light-gray-white)',
              borderRadius: 2,
              '&:hover': {
                borderColor: 'var(--cobalt-blue)',
                backgroundColor: 'var(--cobalt-blue)'
              }
            }}
          >
            Rezervasyonlarım
          </Button>
          
          <Button
            onClick={() => {
              handleSignOut();
              handleDrawerToggle();
            }}
            fullWidth
            variant="contained"
            sx={{
              backgroundColor: 'var(--light-blue-cta)',
              color: 'white',
              borderRadius: 2,
              '&:hover': {
                backgroundColor: 'var(--cobalt-blue)'
              }
            }}
          >
            Çıkış Yap
          </Button>
        </Box>
      ) : (
        <Box sx={{ 
          p: 3, 
          borderTop: '1px solid var(--gray-shadow)'
        }}>
          <Button
            component={Link}
            to="/login"
            onClick={handleDrawerToggle}
            fullWidth
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #005BBD 0%, #0078E7 100%)',
              color: 'white',
              borderRadius: '25px',
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: '0 4px 15px rgba(0, 91, 189, 0.3)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                background: 'linear-gradient(135deg, #004A9F 0%, #005BBD 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(0, 91, 189, 0.4)',
              },
              '&:active': {
                transform: 'translateY(0)',
                boxShadow: '0 4px 15px rgba(0, 91, 189, 0.3)',
              }
            }}
          >
            Giriş Yap
          </Button>
        </Box>
      )}
    </Box>
  );

  return (
    <>
      <AppBar position="sticky" elevation={0} sx={{ 
        backgroundColor: 'var(--navy-blue)', 
        color: 'var(--light-gray-white)',
        borderRadius: 0
      }}>
        <Toolbar>
          <Box
            component={Link}
            to="/"
            sx={{
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
              color: 'inherit',
              gap: 2,
              flexGrow: isMobile ? 1 : 0
            }}
          >
            {/* Logo */}
            <Box
              component="img"
              src={logoWhiteHorizontal}
              alt="HatTravel Logo"
              sx={{
                height: 40,
                width: 'auto',
                maxWidth: 200,
                objectFit: 'contain'
              }}
            />
          </Box>

          {/* Plane GIF - Only visible on desktop */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              justifyContent: 'center',
              flexGrow: 1,
              mx: 2
            }}
          >
            <Box
              component="img"
              src={planeGif}
              alt="Flying Plane"
              sx={{
                height: 40,
                width: '100%',
                objectFit: 'cover',
                objectPosition: 'center',
                opacity: 0.8,
                filter: 'brightness(1.2)'
              }}
            />
          </Box>

          {isMobile ? (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={handleDrawerToggle}
              sx={{ ml: 'auto' }}
            >
              <MenuIcon />
            </IconButton>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {menuItems.map((item) => (
                <Button
                  key={item.text}
                  component={Link}
                  to={item.path}
                  color="inherit"
                  sx={{
                    fontWeight: location.pathname === item.path ? 600 : 400,
                    borderBottom: location.pathname === item.path ? '2px solid' : 'none',
                    borderColor: 'var(--cobalt-blue)',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {item.text}
                </Button>
              ))}

              {/* Language Switcher for Desktop */}
              <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1, mr: 2 }}>
                {languages
                  .filter(l => languageVisibility[l.code])
                  .map(l => (
                    <Tooltip key={l.code} title={l.code.toUpperCase()} placement="bottom">
                      <IconButton
                        size="small"
                        onClick={() => i18n.changeLanguage(l.code)}
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: '50%',
                          fontSize: '1rem',
                          border: '1px solid var(--gray-shadow)',
                          backgroundColor: i18n.language.startsWith(l.code) ? 'var(--cobalt-blue)' : 'transparent',
                          color: i18n.language.startsWith(l.code) ? 'white' : 'var(--light-gray-white)',
                          '&:hover': {
                            backgroundColor: i18n.language.startsWith(l.code) ? 'var(--cobalt-blue)' : 'rgba(255,255,255,0.1)'
                          }
                        }}
                        aria-label={l.code}
                      >
                        {l.flag}
                      </IconButton>
                    </Tooltip>
                  ))}
              </Box>

              {currentUser ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton
                    onClick={handleProfileMenuOpen}
                    sx={{ p: 0 }}
                  >
                    <Avatar
                      sx={{ width: 32, height: 32, bgcolor: 'var(--light-blue-cta)' }}
                    >
                      {userProfile?.displayName?.charAt(0) || currentUser.phoneNumber?.charAt(0) || 'U'}
                    </Avatar>
                  </IconButton>
                </Box>
              ) : (
                <Button
                  component={Link}
                  to="/login"
                  variant="contained"
                  sx={{
                    background: 'linear-gradient(135deg, #005BBD 0%, #0078E7 100%)',
                    color: 'white',
                    borderRadius: '25px',
                    px: 3,
                    py: 1,
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                    boxShadow: '0 4px 15px rgba(0, 91, 189, 0.3)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #004A9F 0%, #005BBD 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 25px rgba(0, 91, 189, 0.4)',
                    },
                    '&:active': {
                      transform: 'translateY(0)',
                      boxShadow: '0 4px 15px rgba(0, 91, 189, 0.3)',
                    }
                  }}
                >
                  Giriş Yap
                </Button>
              )}
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: 240, 
            backgroundColor: 'var(--navy-blue)',
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0
          },
        }}
      >
        {drawer}
      </Drawer>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          '& .MuiPaper-root': {
            backgroundColor: 'var(--light-gray-white)',
          },
        }}
      >
        <MenuItem component={Link} to="/profile" onClick={handleProfileMenuClose}>
          <AccountCircle sx={{ mr: 1 }} />
          Profil
        </MenuItem>
        <MenuItem component={Link} to="/booking-history" onClick={handleProfileMenuClose}>
          <Email sx={{ mr: 1 }} />
          Rezervasyonlarım
        </MenuItem>
        <MenuItem onClick={handleSignOut}>
          Çıkış Yap
        </MenuItem>
      </Menu>
    </>
  );
};

export default Header; 